/**
 * Phase 3 — See
 *
 * Claude reflects exactly ONE pattern. It surfaces as a card that fades in
 * from darkness — the most important micro-interaction in the product.
 * User confirms or redirects. If they redirect, Claude accepts and reflects
 * their version back.
 */
import { useEffect, useRef, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useScreenTrail } from '../../lib/sessionBack'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'
import type { SessionMessage, SessionMode } from '../../types/session'
import { callClaude } from '../../lib/claude'
import { errorCodeOf, guideErrorKey } from '../../lib/guideErrors'
import { parseOptions } from '../../lib/parseOptions'
import { pickVariation } from '../../data/promptVariations'
import LoadingSpiral from '../ui/LoadingSpiral'
import IntuitiveChoice from '../ui/IntuitiveChoice'
import type { ChoiceState } from '../ui/IntuitiveChoice'
import VoiceButton from '../ui/VoiceButton'

const INTERPRETATION_OPTIONS_FALLBACK = [
  '"I\'m not enough"',
  '"I\'m not safe"',
  '"People always leave"',
  '"It\'s all my fault"',
  '"I have to control everything"',
  '"Nothing ever works out for me"',
  '"I have to hold everything together"',
  '"I will be abandoned if I show my needs"',
  '"I am too broken to heal"',
]

// Shown while Claude generates tailored options, and as fallback if generation fails
const COLLAPSE_OPTIONS_FALLBACK = [
  'The need to be right',
  'The blame I\'m carrying',
  'The old story of abandonment',
  'The pressure to figure it all out',
  'The belief that I\'m broken',
  'The fear of being seen',
  'The anger I\'ve been holding',
  'The grief I haven\'t allowed',
  'The need to control the outcome',
  'The shame beneath the story',
]

interface SeeProps {
  sessionId: string
  emotion: string
  intensity: number
  storyText: string
  bodyLocation: string
  reminderText: string
  /** Pre-populated from Feel's Meaning Imprint step — skips the belief collection screen when provided. */
  beliefText?: string
  /** Pre-populated from Feel's Expression Step — passed to Claude context for richer pattern recognition. */
  expressionText?: string
  /**
   * The person's own words for the age of the feeling, from the optional field
   * on the Feel reflection; '' when they gave none. Sent to the guide as user
   * data (see lib/age.ts for what counts as an answer).
   */
  ageText?: string
  intentionText?: string
  firstName?: string
  lifePathNumber?: number
  seriesContext?: string
  /**
   * Awakening-only: short summary of recurring threads from past sessions.
   * Forwarded to callClaude → injected into phase 3 system prompt as
   * background awareness for the guide.
   */
  clusterContext?: string
  /**
   * Mind-level lens — when 'the_work', replaces the standard See flow with
   * Byron Katie's 4-question inquiry. The confirmed belief becomes the
   * patternReflection passed to onComplete.
   */
  sessionMode?: SessionMode
  onComplete: (patternReflection: string, confirmed: boolean) => void
}

type UIState =
  | 'initial-interpretation' | 'loading' | 'pattern-shown' | 'awaiting-confirm'
  | 'deepening' | 'redirecting' | 'redirect-reflecting' | 'reflected' | 'collapsing' | 'error'
  // ── The Work lens states (only when sessionMode === 'the_work') ──
  | 'tw-belief'        // Confirming the belief Claude extracted
  | 'tw-q1' | 'tw-q2' | 'tw-q3' | 'tw-q4'  // Four questions, sequential

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey> = {
  'initial-interpretation': 'see.belief',
  // The pattern card stays up while the follow-up question is fetched.
  'loading':             'see.pattern',
  'pattern-shown':       'see.pattern',
  'deepening':           'see.pattern',
  'awaiting-confirm':    'see.deepening',
  'redirecting':         'see.ownWords',
  'redirect-reflecting': 'see.ownWords',
  'reflected':           'see.ownWordsMirror',
  'collapsing':          'see.collapse',
  'error':               'see.error',
  'tw-belief':           'see.work.belief',
  'tw-q1':               'see.work.q1',
  'tw-q2':               'see.work.q2',
  'tw-q3':               'see.work.q3',
  'tw-q4':               'see.work.q4',
}

export default function See({ sessionId, emotion, intensity, storyText, bodyLocation, reminderText, beliefText, expressionText, ageText, intentionText = '', firstName = '', lifePathNumber, seriesContext, clusterContext, sessionMode, onComplete }: SeeProps) {
  const { t } = useTranslation()

  /*
   * Fallback option sets, read from the locale rather than the module-level
   * constants above (which can never be translated where they sit). Trusted
   * only at the exact expected length, so a short or missing locale array
   * yields the full English set instead of a half-empty picker.
   *
   * Named *Fallback because interpretationOptions and collapseOptions are
   * already useState values holding the Claude-generated personalised set.
   */
  const localArray = (key: string, fallback: string[]) => {
    const v = t(key, { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  }
  const interpretationFallback = localArray('see.interpretationOptions', INTERPRETATION_OPTIONS_FALLBACK)
  const collapseFallback = localArray('see.collapseOptions', COLLAPSE_OPTIONS_FALLBACK)
  // The Work lens jumps straight into [TW-BELIEF] step — Claude extracts the
  // core stressful belief from the story so the user can confirm/edit it.
  // Standard mode preserves prior behaviour: skips belief collection if
  // Meaning Imprint already captured it in Feel.
  const [uiState, setUiState] = useState<UIState>(
    sessionMode === 'the_work'
      ? 'tw-belief'
      : (beliefText ? 'loading' : 'initial-interpretation')
  )
  useScreenId(SCREEN_OF[uiState])
  const [interpretation, setInterpretation] = useState(beliefText ?? '')
  const [interpretationOptions, setInterpretationOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [patternCard, setPatternCard] = useState<string | null>(null)
  const [redirectInput, setRedirectInput] = useState('')
  const [reflectedPattern, setReflectedPattern] = useState<string | null>(null)
  const [deepeningReply, setDeepeningReply] = useState<string | null>(null)
  const [pulseActive, setPulseActive] = useState(false)
  // Why the last call failed, as a code the error screen translates (lib/claude.ts).
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [collapseInput, setCollapseInput] = useState('')
  const [collapseOptions, setCollapseOptions] = useState<string[]>([])
  const [collapseOptionsLoading, setCollapseOptionsLoading] = useState(false)
  // finalPattern tracks which pattern (confirmed or user's redirect) to pass onComplete
  const [finalPattern, setFinalPattern] = useState<{ text: string; confirmed: boolean } | null>(null)

  // ─── The Work state (only used when sessionMode === 'the_work') ──────────
  // twBelief — the confirmed belief that Claude extracted (and user may edit).
  //   This becomes the patternReflection passed to onComplete (the belief IS
  //   the pattern in The Work — that's the whole framework).
  // twQuestion — Claude's current question (different per Q1/Q2/Q3/Q4).
  // twAnswer — user's textarea response for the current question.
  // twAnswers — accumulated answers, keyed by step ('belief'/'q1'-'q4').
  // twLoading — true while fetching Claude's next question.
  const [twBelief, setTwBelief] = useState('')
  const [twQuestion, setTwQuestion] = useState<string | null>(null)
  const [twAnswer, setTwAnswer] = useState('')
  const [twAnswers, setTwAnswers] = useState<Record<string, string>>({})
  const [twLoading, setTwLoading] = useState(false)
  // The question the guide wrote for each Work step after the belief, and what
  // it was written from (the belief and the answers before it). Kept so that
  // going back and continuing with the same answers shows the question that is
  // already written instead of asking the guide for it again.
  // What the person chose on 3.1. Kept here, not in the picker, so it
  // outlives the picker: Back to this screen shows the same chips and words, and
  // Continue with nothing touched hands on the same answer (#68).
  const [beliefPick, setBeliefPick] = useState<ChoiceState | undefined>()

  const [twQuestions, setTwQuestions] = useState<Record<string, { basis: string; text: string }>>({})

  // What each reply was written for, so Back and then Continue on the same
  // words shows the reply already there, and different words ask for a new one.
  const deepeningFor = useRef<string | null>(null)
  const redirectedFor = useRef<string | null>(null)
  const collapseFor = useRef<string | null>(null)

  // ─── Back ──────────────────────────────────────────────────────────────────
  // The screens See has shown, in order (lib/sessionBack.ts). The Work's
  // questions share one question slot and one answer box, and the redirect
  // screens share the box where the person puts the pattern their own way, so
  // each screen is captured with its own and put back with them.
  useScreenTrail<UIState, { twBelief: string; twQuestion: string | null; twAnswer: string; redirectInput: string }>({
    screen: uiState,
    go: setUiState,
    kind: s =>
      s === 'loading' || s === 'deepening' || s === 'redirect-reflecting' ? 'loading'
        : s === 'error' ? 'error'
        : s.startsWith('tw-') && twLoading ? 'loading'
        : 'rest',
    capture: () => ({ twBelief, twQuestion, twAnswer, redirectInput }),
    restore: slot => {
      setTwBelief(slot.twBelief)
      setTwQuestion(slot.twQuestion)
      setTwAnswer(slot.twAnswer)
      setRedirectInput(slot.redirectInput)
    },
  })

  // sessionMode threaded to Claude so the Edge Function selects the right
  // case-3 prompt branch (standard vs The Work).
  // clusterContext: Awakening-only background awareness — injected by the
  // Edge Function into the phase 3 system prompt; never referenced directly.
  const opts = { phase: 3, emotion, intensity, firstName, intentionText, sessionId, lifePathNumber, seriesContext, clusterContext, sessionMode, ageText: ageText || undefined }

  const interpretationContext = interpretation
    ? ` They believe: ${interpretation}.`
    : ''

  const beliefContext = beliefText ? ` Core belief surfaced: "${beliefText}".` : ''
  const expressionContext = expressionText ? ` Uncensored expression: "${expressionText}".` : ''

  const contextMessages: SessionMessage[] = [
    { role: 'user', content: storyText },
    { role: 'assistant', content: `I hear you. You feel this in your ${bodyLocation}. It reminds you of: ${reminderText}.${interpretationContext}${beliefContext}${expressionContext}` },
    { role: 'user', content: '[Begin phase — reflect one pattern]' },
  ]

  useEffect(() => {
    const storyExcerpt = storyText.slice(0, 300)
    callClaude({
      phase: 20, emotion, intensity,
      messages: [{ role: 'user', content: '[Generate options]' }],
      firstName, sessionId,
      optionContext: `Question: "What do you believe about yourself or the world based on this?" They shared: "${storyExcerpt}". Generate 9 core belief statements this person might be holding — drawn from what they actually wrote, not from generic templates. VARY the structure: some start with "I...", some with "People...", some with "The world...", some with "Love...", "Nothing...", etc. Each belief must feel specific to this person's situation. No two beliefs should be variations of the same theme. Do not use generic affirmations.`,
    }).then(result => {
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        if (parsed.length >= 7) setInterpretationOptions(parsed)
        else setInterpretationOptions(interpretationFallback)
      } else {
        setInterpretationOptions(interpretationFallback)
      }
      setOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (uiState !== 'loading') return
    let cancelled = false
    callClaude({ ...opts, messages: contextMessages }).then(result => {
      if (cancelled) return
      if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
      setPatternCard(result.response)
      setUiState('pattern-shown')
      setTimeout(() => setPulseActive(true), 600)
      setTimeout(() => setPulseActive(false), 2200)
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState, retryCount])

  // ─── The Work — fetch [TW-BELIEF] when entering tw-belief screen ─────────
  // The Edge Function's case 3 prompt branch (when sessionMode === 'the_work')
  // returns the extracted belief as a single sentence + a confirm question.
  useEffect(() => {
    if (uiState !== 'tw-belief' || sessionMode !== 'the_work' || twQuestion) return
    let cancelled = false
    setTwLoading(true)
    const beliefMsgs: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: `Body location: ${bodyLocation}. Memory: ${reminderText}.${beliefText ? ` Surfaced belief: "${beliefText}".` : ''}${expressionText ? ` Expression: "${expressionText}".` : ''}` },
      { role: 'user', content: '[TW-BELIEF]' },
    ]
    callClaude({ ...opts, messages: beliefMsgs }).then(result => {
      if (cancelled) return
      setTwLoading(false)
      if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
      // Parse out the suggested belief — Claude's response is "<belief>\n\nIs this the thought..."
      // We seed twBelief with the first line so the textarea is editable.
      const lines = result.response.split('\n').map(l => l.trim()).filter(Boolean)
      const suggestedBelief = lines[0] || result.response.trim()
      setTwBelief(suggestedBelief)
      setTwQuestion(result.response)
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState])

  // ─── The Work — handle advancing through Q1 → Q2 → Q3 → Q4 ───────────────
  // Each call sends the [TW-Qn] tag plus the confirmed belief and prior answers.
  // Claude responds with the next question; we display it and accept the answer.
  async function handleTwAdvance(currentStep: 'belief' | 'q1' | 'q2' | 'q3' | 'q4') {
    // Determine next-step tag and target UI state
    const nextTag =
      currentStep === 'belief' ? '[TW-Q1]' :
      currentStep === 'q1' ? '[TW-Q2]' :
      currentStep === 'q2' ? '[TW-Q3]' :
      currentStep === 'q3' ? '[TW-Q4]' :
      null  // q4 has no "next" — emits onComplete instead

    // Final step (after Q4 answer): the confirmed belief becomes the patternReflection.
    // Why: in The Work, the belief IS the pattern — that's the whole framework.
    // Downstream Shift turnaround uses this to generate "what's the opposite of X" reflections.
    if (currentStep === 'q4') {
      const finalAnswers = { ...twAnswers, q4: twAnswer.trim() }
      setTwAnswers(finalAnswers)
      onComplete(twBelief.trim(), true)
      return
    }

    const steps = ['belief', 'q1', 'q2', 'q3', 'q4'] as const
    const nextStep = steps[steps.indexOf(currentStep) + 1]
    const laterSteps = steps.slice(steps.indexOf(currentStep) + 1)

    // Snapshot updated answers before async setState
    const updatedAnswers: Record<string, string> = currentStep === 'belief'
      ? { ...twAnswers }  // No answer yet for belief — that screen just confirms
      : { ...twAnswers, [currentStep]: twAnswer.trim() }

    // What the next question is written from: the belief and the answers so far.
    const basis = JSON.stringify([
      twBelief.trim(),
      ...steps.slice(1, steps.indexOf(currentStep) + 1).map(step => updatedAnswers[step] ?? ''),
    ])

    // Back can bring the person to a question they have already answered. With
    // the same answers, the next question is already written: show it, with the
    // answer they gave there, and do not ask the guide again.
    const written = twQuestions[nextStep]
    if (written && written.basis === basis) {
      setTwAnswers(updatedAnswers)
      setTwQuestion(written.text)
      setTwAnswer(updatedAnswers[nextStep] ?? '')
      setUiState(`tw-${nextStep}` as UIState)
      return
    }

    // New or changed answers: anything after this step was written from the old
    // ones, so it is dropped and asked for afresh as the person goes on.
    for (const later of laterSteps) delete updatedAnswers[later]
    setTwQuestions(prev => {
      const kept = { ...prev }
      for (const later of laterSteps) delete kept[later]
      return kept
    })
    setTwAnswers(updatedAnswers)
    setTwAnswer('')
    setTwLoading(true)

    // Build full conversation history so Claude has belief + all prior Q&A
    const msgs: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: `Body: ${bodyLocation}. Memory: ${reminderText}.` },
      { role: 'user', content: `[TW-BELIEF confirmed] ${twBelief.trim()}` },
    ]
    if (updatedAnswers.q1) msgs.push({ role: 'user', content: `[TW-Q1 answer] ${updatedAnswers.q1}` })
    if (updatedAnswers.q2) msgs.push({ role: 'user', content: `[TW-Q2 answer] ${updatedAnswers.q2}` })
    if (updatedAnswers.q3) msgs.push({ role: 'user', content: `[TW-Q3 answer] ${updatedAnswers.q3}` })
    msgs.push({ role: 'user', content: nextTag! })

    const result = await callClaude({ ...opts, messages: msgs })
    setTwLoading(false)
    if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
    setTwQuestion(result.response)
    setTwQuestions(prev => ({ ...prev, [nextStep]: { basis, text: result.response } }))

    const nextState: UIState =
      currentStep === 'belief' ? 'tw-q1' :
      currentStep === 'q1' ? 'tw-q2' :
      currentStep === 'q2' ? 'tw-q3' :
      'tw-q4'
    setUiState(nextState)
  }

  // Generate collapse options tailored to the named pattern once we enter the collapsing state.
  // Both uiState and finalPattern are set together so they will be in sync when this fires.
  useEffect(() => {
    if (uiState !== 'collapsing' || !finalPattern) return
    // Back and forward through this screen must not ask again for options
    // written for the same pattern.
    if (collapseFor.current === finalPattern.text) return
    collapseFor.current = finalPattern.text
    setCollapseOptionsLoading(true)
    const lpNote = lifePathNumber
      ? ` Their life path number is ${lifePathNumber} — weave in 1-2 options that speak to the shadow or growth edge of life path ${lifePathNumber}.`
      : ''
    callClaude({
      phase: 20,
      emotion,
      intensity,
      messages: [{ role: 'user', content: '[Generate release options]' }],
      firstName,
      sessionId,
      lifePathNumber,
      optionContext: `The person has just named their core pattern: "${finalPattern.text}". They are feeling "${emotion}" (intensity ${intensity}/10), held in their ${bodyLocation}.${lpNote} Generate 9 short release phrases — things this specific person might be ready to let go of, drawn directly from their named pattern. Each phrase must be under 9 words. VARY the structure: some start with "The...", some with "My...", some with "I...", some open with a feeling or behavior word. Every phrase must be specific to "${finalPattern.text}" — if it could appear in any session regardless of pattern, rewrite it until it can't. No two phrases should feel like rephrasing of the same thing.`,
    }).then(result => {
      if (collapseFor.current !== finalPattern.text) return  // pattern changed meanwhile
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        setCollapseOptions(parsed.length >= 7 ? parsed : collapseFallback)
      } else {
        setCollapseOptions(collapseFallback)
      }
      setCollapseOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState, finalPattern])

  // User confirms the pattern — ask one deepening question
  async function handleConfirm() {
    if (!patternCard) return
    // Back can bring the person here after the guide has already answered "yes,
    // that lands" for this pattern. Show that answer again.
    if (deepeningReply && deepeningFor.current === patternCard) {
      setUiState('awaiting-confirm')
      return
    }
    setUiState('deepening')
    const msgs: SessionMessage[] = [
      ...contextMessages,
      { role: 'assistant', content: patternCard },
      { role: 'user', content: 'Yes, that lands.' },
    ]
    const result = await callClaude({ ...opts, messages: msgs })
    if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
    setDeepeningReply(result.response)
    deepeningFor.current = patternCard
    setUiState('awaiting-confirm')
  }

  async function handleRedirect() {
    if (!redirectInput.trim() || uiState === 'redirect-reflecting') return
    // Same words as the reflection already on hand: show it again.
    if (reflectedPattern && redirectedFor.current === redirectInput.trim()) {
      setUiState('reflected')
      return
    }
    setUiState('redirect-reflecting')
    const msgs: SessionMessage[] = [
      ...contextMessages,
      { role: 'assistant', content: patternCard! },
      { role: 'user', content: redirectInput.trim() },
    ]
    const result = await callClaude({ ...opts, messages: msgs })
    if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
    setReflectedPattern(result.response)
    redirectedFor.current = redirectInput.trim()
    setUiState('reflected')
  }

  // ─── The Work flow — gated behind sessionMode === 'the_work' ────────────
  // Renders a single Socratic question per screen. Each screen uses the See
  // deep-purple accent (#7B40A8) for visual continuity. The belief screen is
  // editable; Q1-Q4 are textarea responses.
  if (
    uiState === 'tw-belief' || uiState === 'tw-q1' || uiState === 'tw-q2' ||
    uiState === 'tw-q3' || uiState === 'tw-q4'
  ) {
    // Map UI state → step key for handler dispatch and progress display
    const stepKey: 'belief' | 'q1' | 'q2' | 'q3' | 'q4' =
      uiState === 'tw-belief' ? 'belief' :
      uiState === 'tw-q1' ? 'q1' :
      uiState === 'tw-q2' ? 'q2' :
      uiState === 'tw-q3' ? 'q3' :
      'q4'

    // Numeric step index for the progress label — belief is step 1, q1 = step 2, etc.
    const stepNumber =
      stepKey === 'belief' ? 1 :
      stepKey === 'q1' ? 2 :
      stepKey === 'q2' ? 3 :
      stepKey === 'q3' ? 4 : 5

    // Continue is gated by: not loading, AND
    //   - on belief screen: twBelief must have content
    //   - on Q1-Q4 screens: twAnswer must have content
    const canContinue = !twLoading && (
      stepKey === 'belief' ? twBelief.trim().length > 0 : twAnswer.trim().length > 0
    )

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          {/* Supertitle — See deep-purple accent */}
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#7B40A8' }}>
            {t('arrival.modeInquiry')}
          </p>

          {/* Progress label */}
          <p className="text-xs mb-5" style={{ color: '#9B8FAA' }}>
            {stepKey === 'belief' ? t('see.identifyBelief') : t('see.questionOfFour', { n: stepNumber - 1 })}
          </p>

          {/* Claude's question / belief proposal — italic display font */}
          {twLoading ? (
            <div className="mb-8">
              <LoadingSpiral size={22} />
            </div>
          ) : (
            twQuestion && (
              <div className="mb-8 message-appear">
                <p className="text-base leading-relaxed font-display italic whitespace-pre-line" style={{ color: '#1C1128' }}>
                  {twQuestion}
                </p>
              </div>
            )
          )}

          {/* Input — belief screen edits twBelief; question screens edit twAnswer */}
          {!twLoading && (
            <div className="rounded-2xl px-5 py-5 message-appear" style={{
              background: 'rgba(123,64,168,0.04)',
              borderTop: '3px solid #7B40A8',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#7B40A8' }}>
                {stepKey === 'belief' ? t('see.theThought') : t('see.yourResponse')}
              </p>
              <VoiceButton
                onTranscript={t => stepKey === 'belief'
                  ? setTwBelief(prev => prev ? `${prev} ${t}` : t)
                  : setTwAnswer(prev => prev ? `${prev} ${t}` : t)}
                accentColor="#7B40A8"
              >
                <textarea
                  value={stepKey === 'belief' ? twBelief : twAnswer}
                  onChange={e => stepKey === 'belief' ? setTwBelief(e.target.value) : setTwAnswer(e.target.value)}
                  placeholder={stepKey === 'belief' ? t('see.editIfNeeded') : t('see.takeYourTime')}
                  rows={stepKey === 'belief' ? 2 : 4}
                  maxLength={2000}
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                  style={{ borderColor: '#7B40A8', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                  onFocus={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = '0 0 0 3px rgba(123,64,168,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = 'none' }}
                />
              </VoiceButton>
            </div>
          )}

          <div className="flex-1" />

          <button
            onClick={() => handleTwAdvance(stepKey)}
            disabled={!canContinue}
            className="w-full py-4 rounded-xl font-medium tracking-wide transition-all"
            style={{
              background: canContinue ? '#3D1F62' : '#E4D5EE',
              color: canContinue ? 'white' : '#9B8FAA',
              border: 'none',
              cursor: canContinue ? 'pointer' : 'not-allowed',
            }}
          >
            {stepKey === 'belief' ? t('see.thisIsThought') : t('session.continue')}
          </button>
        </div>
      </div>
    )
  }

  if (uiState === 'initial-interpretation') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
          <IntuitiveChoice
            initialState={beliefPick}
            onStateChange={setBeliefPick}
            prompt={t('see.beliefQuestion')}
            options={interpretationOptions}
            isLoadingOptions={optionsLoading}
            placeholder={t('see.orDescribeBelief')}
            ctaLabel={t('session.continue')}
            skipLabel={t('see.skipContinue')}
            onComplete={value => {
              // Back can bring the person here after the pattern was written. The
              // same belief shows that pattern again; a different one discards it
              // and everything written from it, and asks afresh.
              if (patternCard && value === interpretation) {
                setUiState('pattern-shown')
                return
              }
              setInterpretation(value)
              setPatternCard(null)
              setDeepeningReply(null)
              setReflectedPattern(null)
              setFinalPattern(null)
              setUiState('loading')
            }}
          />
        </div>
      </div>
    )
  }

  if (uiState === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 page-enter">
        <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>{t('errors.somethingWrong')}</p>
        {errorCode && (
          <p className="text-xs mb-4 text-center max-w-xs" style={{ color: '#6B5F7A' }}>{t(guideErrorKey(errorCode))}</p>
        )}
        <button
          onClick={() => { setErrorCode(null); setUiState('loading'); setRetryCount(c => c + 1) }}
          style={{ background: '#3D1F62', color: 'white', border: 'none', borderRadius: '9999px', padding: '0.75rem 1.5rem', cursor: 'pointer', fontSize: '0.875rem' }}
        >
          {t('errors.tryAgain')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {/* Loading */}
        {uiState === 'loading' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <LoadingSpiral size={28} label={t('see.patternEmerging')} />
          </div>
        )}

        {/* Pattern emerge card — visible until user confirms or redirects */}
        {(uiState === 'pattern-shown' || uiState === 'deepening' || uiState === 'redirecting' || uiState === 'redirect-reflecting' || uiState === 'reflected') && patternCard && (
          <div className="flex-1 flex flex-col">

            {/* The card */}
            <div
              className={`mb-8 px-6 py-7 rounded-2xl message-appear ${pulseActive ? 'spiral-pulse' : ''}`}
              style={{
                background: 'linear-gradient(145deg, #FFFFFE 0%, #FAF5FF 45%, #F0F5FF 100%)',
                borderTop: '3px solid #7B40A8',
                borderRight: '1.5px solid #CEBCDC',
                borderBottom: '1.5px solid #CEBCDC',
                borderLeft: '1.5px solid #CEBCDC',
                boxShadow: '0 4px 32px rgba(123,64,168,0.10)',
              }}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] mb-1" style={{ color: '#C4B5D4' }}>
                {t('see.satoriMoment')}
              </p>
              <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
                {pickVariation(t('variations.patternHeader', { returnObjects: true }), sessionId)}
              </p>
              <p className="text-lg leading-relaxed font-display" style={{ color: '#1C1128', fontStyle: 'italic' }}>
                {patternCard}
              </p>
            </div>

            {/* Initial confirm/redirect choice */}
            {uiState === 'pattern-shown' && (
              <div className="flex flex-col gap-3 message-appear">
                <p className="text-sm text-center mb-2" style={{ color: '#6B5F7A' }}>
                  {t('see.doesThisLand')}
                </p>
                <button
                  onClick={handleConfirm}
                  className="w-full py-4 rounded-xl font-medium tracking-wide transition-all"
                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                >
                  {t('see.yesThatLands')}
                </button>
                <button
                  onClick={() => setUiState('redirecting')}
                  className="w-full py-3 rounded-xl text-sm transition-all"
                  style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                >
                  {t('see.putDifferently')}
                </button>
              </div>
            )}

            {/* Deepening — waiting for Claude's follow-up question */}
            {uiState === 'deepening' && (
              <div className="message-appear">
                <LoadingSpiral size={22} />
              </div>
            )}

            {/* Redirect input */}
            {uiState === 'redirecting' && (
              <div className="flex flex-col gap-3 message-appear">
                {/* Reflection textarea — wrapped in See deep purple card (#7B40A8) at 4% tint */}
                <div className="rounded-2xl px-5 py-5" style={{
                  background: 'rgba(123,64,168,0.04)',
                  borderTop: '3px solid #7B40A8',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}>
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#7B40A8' }}>
                    {t('see.yourReflection')}
                  </p>
                  <VoiceButton
                    onTranscript={t => setRedirectInput(prev => prev ? `${prev} ${t}` : t)}
                    accentColor="#7B40A8"
                  >
                    <textarea
                      value={redirectInput}
                      onChange={e => setRedirectInput(e.target.value)}
                      placeholder={t('see.inYourOwnWords')}
                      rows={3}
                      maxLength={1000}
                      autoFocus
                      className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                      style={{ borderColor: '#7B40A8', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                      onFocus={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = '0 0 0 3px rgba(123,64,168,0.18)' }}
                      onBlur={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = 'none' }}
                    />
                  </VoiceButton>
                </div>
                <button
                  onClick={handleRedirect}
                  disabled={!redirectInput.trim()}
                  className="w-full py-4 rounded-xl font-medium tracking-wide"
                  style={{
                    background: redirectInput.trim() ? '#3D1F62' : '#E4D5EE',
                    color: redirectInput.trim() ? 'white' : '#9B8FAA',
                    border: 'none',
                    cursor: redirectInput.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  {t('session.continue')}
                </button>
                <button
                  onClick={() => { setRedirectInput(''); setUiState('pattern-shown') }}
                  className="w-full py-3 rounded-xl text-sm"
                  style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
                >
                  {t('see.originalLands')}
                </button>
              </div>
            )}

            {/* Loading spiral while Claude reflects redirect */}
            {uiState === 'redirect-reflecting' && (
              <div className="message-appear">
                <LoadingSpiral size={22} />
              </div>
            )}

            {/* Claude reflects user's version — offer confirmation or another try */}
            {uiState === 'reflected' && reflectedPattern && (
              <div className="flex flex-col gap-4 message-appear">
                <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
                  {reflectedPattern}
                </p>
                <button
                  onClick={() => { setFinalPattern({ text: redirectInput.trim(), confirmed: false }); setUiState('collapsing') }}
                  className="w-full py-4 rounded-xl font-medium tracking-wide"
                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                >
                  {t('see.yesThatsIt')}
                </button>
                <button
                  onClick={() => { setRedirectInput(''); setUiState('redirecting') }}
                  className="w-full py-3 rounded-xl text-sm"
                  style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
                >
                  {t('see.letMeTryAgain')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Deepening question — pattern card is gone, this has full focus */}
        {uiState === 'awaiting-confirm' && deepeningReply && (
          <div className="flex-1 flex flex-col message-appear">
            <p className="text-base leading-relaxed font-display italic mb-10"
              style={{ color: '#1C1128' }}>
              {deepeningReply}
            </p>
            <div className="flex-1" />
            <button
              onClick={() => { setFinalPattern({ text: patternCard!, confirmed: true }); setUiState('collapsing') }}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('session.continue')}
            </button>
          </div>
        )}

        {/* ── Collapsing the Story ──────────────────────────────────────── */}
        {uiState === 'collapsing' && (
          <div className="flex-1 flex flex-col message-appear">
            <div className="mb-8">
              <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>{t('see.beforeDeeperLabel')}</p>
            </div>

            <p className="font-display text-xl leading-snug mb-3"
              style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
              {t('see.storySoften')}
            </p>
            <p className="text-sm leading-relaxed mb-8" style={{ color: '#6B5F7A' }}>
              {t('see.breathInstruction')}
            </p>

            {/* The pattern being released — visible context */}
            <div
              className="mb-6 px-4 py-3 rounded-xl"
              style={{
                // Deep-purple tinted background — See phase accent at ~4% opacity
                background: 'rgba(123,64,168,0.04)',
                // Left-border accent recipe for small inline context cards
                borderTop: '1.5px solid #E4D5EE',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '3px solid #7B40A8',
              }}
            >
              <p className="text-xs mb-1" style={{ color: '#9B8FAA' }}>{t('see.patternLabel')}</p>
              <p className="text-sm leading-relaxed italic" style={{ color: '#3D1F62' }}>{finalPattern?.text}</p>
            </div>

            <p className="text-base leading-relaxed mb-6 font-display italic"
              style={{ color: '#1C1128' }}>
              {t('see.releaseQuestion')}
            </p>

            {collapseOptionsLoading ? (
              /* Skeleton chips while Claude generates — 9 placeholders at varying widths */
              <div className="flex flex-wrap gap-2 mb-4">
                {[88, 120, 100, 145, 96, 130, 110, 88, 140].map((w, i) => (
                  <div
                    key={i}
                    style={{
                      width: w,
                      height: 36,
                      borderRadius: 9999,
                      background: '#F4EFF8',
                      border: '1.5px solid #E4D5EE',
                      animation: `pulse 1.4s ease-in-out ${i * 0.1}s infinite`,
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mb-4">
                {collapseOptions.map(opt => {
                  const isSelected = collapseInput.includes(opt)
                  return (
                    <button
                      key={opt}
                      onClick={() => {
                        setCollapseInput(prev =>
                          isSelected
                            ? prev.replace(opt, '').replace(/^,\s*|,\s*$/, '').replace(/,\s*,/, ',').trim()
                            : prev ? `${prev}, ${opt}` : opt
                        )
                      }}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                      style={{
                        background: isSelected ? '#3D1F62' : 'white',
                        color: isSelected ? 'white' : '#6B5F7A',
                        border: isSelected ? '1.5px solid #3D1F62' : '1.5px solid #C4B5D4',
                        cursor: 'pointer',
                      }}
                    >
                      {opt}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Collapse / release textarea — wrapped in See deep purple card (#7B40A8) at 4% tint */}
            <div className="rounded-2xl px-5 py-5" style={{
              background: 'rgba(123,64,168,0.04)',
              borderTop: '3px solid #7B40A8',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#7B40A8' }}>
                {t('see.release')}
              </p>
              <VoiceButton
                onTranscript={t => setCollapseInput(prev => prev ? `${prev} ${t}` : t)}
                accentColor="#7B40A8"
              >
                <textarea
                  value={collapseInput}
                  onChange={e => setCollapseInput(e.target.value)}
                  placeholder={t('see.releasePlaceholder')}
                  rows={3}
                  maxLength={1000}
                  className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                  style={{ borderColor: '#7B40A8', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
                  onFocus={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = '0 0 0 3px rgba(123,64,168,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#7B40A8'; e.target.style.boxShadow = 'none' }}
                />
              </VoiceButton>
            </div>

            {collapseInput.trim() && (
              <p className="text-xs mt-2 message-appear" style={{ color: '#C4B5D4' }}>
                {t('see.noticeLighter')}
              </p>
            )}

            <div style={{ flex: 1 }} />

            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={() => onComplete(finalPattern!.text, finalPattern!.confirmed)}
                className="w-full py-4 rounded-xl font-medium tracking-wide"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

